import type { ReactNode } from 'react';

import { IdCardMock } from '../IdCardMock';
import { DobField, Field } from '../PreviewFields';
import { PreviewHeader } from '../PreviewHeader';

export interface PanFormValues {
  panNumber: string;
  fullName: string;
  dob: string;
}

export interface PanVerificationStandardProps {
  dob: string;
  setDob: (v: string) => void;
  /** Live form values — pass from the session runtime; omit in the builder mock. */
  values?: Pick<PanFormValues, 'panNumber' | 'fullName'>;
  onChange?: (patch: Partial<PanFormValues>) => void;
  /**
   * Card shown above the form. Defaults to the static designer mock (builder
   * preview); the session runtime passes a live card that fills in as the
   * applicant types.
   */
  card?: ReactNode;
  /** PAN format validation message (session runtime) — shown under the field. */
  panError?: string;
  /** DOB required (Return-Data has date_of_birth/age enabled) → shows `*`. */
  dobRequired?: boolean;
}

// Designer's PanVerificationStandard screen: header + PAN card mock +
// PAN Number / Full Name / DOB fields + privacy notice.
export function PanVerificationStandard({
  dob,
  setDob,
  values,
  onChange,
  card,
  panError,
  dobRequired,
}: PanVerificationStandardProps) {
  return (
    <>
      <PreviewHeader title="PAN Verification" subtitle="Please enter your PAN Details" compact />
      <div className="mb-2 w-full">{card ?? <IdCardMock />}</div>
      {/* A little more air between the three inputs, and ONLY on this screen.
          Removing the stray 16px flex gaps left the PAN form short of the viewport with
          room to spare, so 8px of that goes back where it reads best — between the
          fields, which were tighter than the rest of the screen's rhythm.
          `gap-2` on the group rather than a bigger margin on `Field`: that margin is
          shared by every step, and the screens that were always tight (Aadhaar Secure QR,
          ID Verification) have no room to give. This buys 16px total across two gaps —
          well inside what was recovered, so the form still fits without scrolling. */}
      <div className="flex flex-col gap-2">
        <Field
          label="PAN Number"
          placeholder="ABCDE1234F"
          value={values?.panNumber}
          onChange={onChange ? (v) => onChange({ panNumber: v.toUpperCase() }) : undefined}
          error={panError}
        />
        <Field
          label="Full Name"
          placeholder="Smith Jhon"
          value={values?.fullName}
          onChange={onChange ? (v) => onChange({ fullName: v }) : undefined}
        />
        <DobField dob={dob} setDob={setDob} required={dobRequired} />
      </div>
      <p className="mb-3 mt-2 text-[11px] leading-snug text-[#6B7280]">
        Your PAN details are verified securely. We do not store your personal information beyond
        what is required by law.
      </p>
    </>
  );
}
